<script lang="ts">
  /**
   * The row detail side panel (design 6.4, as amended by #2222): why this document is on the list,
   * who owns it, and the two actions the Permissions Assurance finding flyover offers on it -
   * "View document and manage access" and "Contact document author".
   *
   * Its one write is the review status (#2253): `ExposureStatusBlock` sits above the owner and
   * exits, and saves through the container's `onSaveStatus` (the container owns the service call
   * and the store patch). There is no feedback control and no Sensitive Data Detection exit (that
   * page has no per-document view). Everything shown is already on the row, so opening the panel
   * performs no Firestore read and the whole component graph stays firebase-free and mountable in
   * a rendered test. `findingIds`
   * stays on the row but the panel does not read it: there is no jump to Permissions Assurance.
   *
   * There is no `Sheet` primitive in `$lib/ui`, and the one drawer there is a modal bottom sheet
   * bound to a global store. The design asks for a panel that *narrows* the table, so this is a
   * plain `<aside>`; the container owns where it sits.
   */
  import { Tooltip, TooltipContent, TooltipTrigger } from "$lib/ui/tooltip";
  import { formatNumberWithCommas } from "$lib/utils/number";

  import { categoryColour, type NamedCustomInfoType } from "../categoryLabels";
  import { ownerContactOf, panelFindingsTotal, panelInfoTypeGroups } from "../exposedSensitiveDocuments";
  import ExposureBadge from "./ExposureBadge.svelte";
  import ExternalPrincipalsList from "./ExternalPrincipalsList.svelte";
  import ExposureStatusBlock from "./ExposureStatusBlock.svelte";
  import type { ExposedDocumentWithStatus, StatusLoadState } from "./status";
  import type { SaveStatus } from "./statusSave";

  /** The flyover's own tooltip wording for a disabled contact control. */
  const NO_VALID_EMAIL = "No valid email available.";

  let {
    row,
    activeInfoTypes = null,
    customInfoTypes = [],
    statusState = "ready",
    publishedAt,
    onSaveStatus,
    onClose,
  }: {
    /** The joined row: `row.status` is its status record, none when it is `open`. */
    row: ExposedDocumentWithStatus;
    /** The org's active info-type list (#2206). Null means show the whole count map. */
    activeInfoTypes?: Set<string> | null;
    /** The org's enabled custom info types, so a custom name groups as its own category. */
    customInfoTypes?: NamedCustomInfoType[];
    /** The status store's load state; `failed` makes the block read "Status unavailable". */
    statusState?: StatusLoadState;
    /** The row's provider publish time (D2 of the reopened line), when its metadata reads `published`. */
    publishedAt?: Date;
    /** Saves a status. Without it the panel shows no status block. */
    onSaveStatus?: SaveStatus;
    onClose: () => void;
  } = $props();

  const groups = $derived(panelInfoTypeGroups(row, { activeInfoTypes: activeInfoTypes ?? undefined, customInfoTypes }));
  /** The trimmed sum under an active list, so the heading agrees with the list and the row. */
  const findingsTotal = $derived(panelFindingsTotal(row, groups, activeInfoTypes));
  const owner = $derived(ownerContactOf(row));
</script>

<aside
  class="flex w-[26rem] shrink-0 flex-col gap-4 overflow-auto rounded-lg bg-white p-4 text-sm text-offblack shadow"
  data-testid="exposed-document-detail-panel"
  data-row-id={row.id}
  aria-label="Exposed document detail"
>
  <div class="flex items-start justify-between gap-2">
    <div class="min-w-0">
      <h3 class="truncate font-semibold" title={row.name}>{row.name}</h3>
      <p class="break-all font-mono text-[10px] text-gray-500">{row.path}</p>
    </div>
    <button
      type="button"
      class="shrink-0 text-xs text-gray-500 hover:text-offblack"
      data-testid="exposed-document-detail-close"
      onclick={onClose}
    >
      Close
    </button>
  </div>

  <section class="flex flex-col gap-2">
    <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Exposure</h4>
    <div class="flex items-center gap-2">
      <ExposureBadge {row} />
      <span class="text-xs" data-testid="exposed-document-detail-public">
        {row.isPublic ? "Public: anyone with the link can open it" : "Not public"}
      </span>
    </div>
    <ExternalPrincipalsList {row} />
  </section>

  <section class="flex flex-col gap-2">
    <h4
      class="text-xs font-semibold uppercase tracking-wide text-gray-500"
      data-testid="exposed-document-categories-heading"
    >
      Categories · {formatNumberWithCommas(findingsTotal)} instances total
    </h4>
    {#if groups.length === 0}
      <p class="text-xs text-gray-500" data-testid="exposed-document-categories-empty">
        No categories to show for this document.
      </p>
    {:else}
      {#each groups as group (group.category)}
        <div data-testid="exposed-document-category" data-category={group.category}>
          <span
            class="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-black/10 px-1.5 py-0 text-[10px] font-medium text-offblack"
            style="background:{categoryColour(group)}"
            title={group.detectors.map((detector) => detector.displayName).join("\n")}
            data-testid="exposed-document-category-line"
          >
            {group.label}
            <span class="rounded bg-white/70 px-1 font-semibold tabular-nums"
              >{formatNumberWithCommas(group.count)}</span
            >
          </span>
          <!-- Detectors by Google display name, never the raw name, not even on hover (#2250). The
               hover shows a truncated name in full. A category of one detector would only repeat
               its own line, so it shows the category line alone. -->
          {#if group.detectors.length > 1}
            <ul class="ml-3 border-l border-gray-200 pl-2 text-xs">
              {#each group.detectors as detector (detector.infoType)}
                <li class="flex items-center justify-between gap-2" data-testid="exposed-document-detector">
                  <span class="min-w-0 truncate" title={detector.displayName}>{detector.displayName}</span>
                  <span class="shrink-0 tabular-nums">{formatNumberWithCommas(detector.count)}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
      {/each}
    {/if}
  </section>

  {#if onSaveStatus}
    <section class="flex flex-col gap-2 border-t border-gray-200 pt-3" data-testid="exposed-document-status-section">
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</h4>
      <ExposureStatusBlock {row} {statusState} {publishedAt} onSave={onSaveStatus} />
    </section>
  {/if}

  <section class="flex flex-col gap-2 border-t border-gray-200 pt-3">
    <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Document owner</h4>
    {#if owner.state !== "unknown"}
      <p class="break-all text-xs" data-testid="exposed-document-owner-email">{owner.email}</p>
    {:else}
      <p class="text-xs text-gray-500" data-testid="exposed-document-owner-unknown">
        Owner unknown: no owner email was published for this document.
      </p>
    {/if}

    <div class="flex flex-wrap gap-2">
      {#if owner.state === "valid"}
        <a
          href={owner.href}
          title={owner.email}
          class="inline-flex w-fit items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-semibold text-white"
          data-testid="exposed-document-contact-author"
        >
          Contact document author
        </a>
      {:else}
        <!-- A natively disabled button leaves the tab order, so keyboard users would never reach the
             reason. The tooltip trigger is the control: aria-disabled, focusable, and described by
             the reason, which the tooltip also shows on focus or hover. -->
        <Tooltip>
          <TooltipTrigger
            aria-disabled="true"
            aria-describedby="exposed-document-contact-author-reason"
            class="inline-flex w-fit cursor-not-allowed items-center gap-1 rounded bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-500"
            data-testid="exposed-document-contact-author"
          >
            Contact document author
          </TooltipTrigger>
          <TooltipContent>
            <p data-testid="exposed-document-contact-author-tooltip">{NO_VALID_EMAIL}</p>
          </TooltipContent>
        </Tooltip>
        <span id="exposed-document-contact-author-reason" class="sr-only">{NO_VALID_EMAIL}</span>
      {/if}

      {#if row.link}
        <a
          href={row.link}
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex w-fit items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-offblack hover:bg-zinc-50"
          data-testid="exposed-document-manage-access"
        >
          View document and manage access
        </a>
      {:else}
        <!-- `link` is optional on the row, and an anchor with an empty href just reloads this page. -->
        <p class="text-xs text-gray-500" data-testid="exposed-document-manage-access-missing">
          No document link was published for this row.
        </p>
      {/if}
    </div>
  </section>
</aside>
